import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Форматирование прошедшего времени
 * @param {number} ms - миллисекунды
 * @returns {string} - "X.X с" (до минуты) или "мм:сс.д"
 */
export const formatElapsed = (ms) => {
  if (ms == null || isNaN(ms)) return '';
  if (ms < 60000) {
    return `${(ms / 1000).toFixed(1)} с`;
  }
  const totalSeconds = ms / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = (totalSeconds % 60).toFixed(1).padStart(4, '0');
  return `${String(minutes).padStart(2, '0')}:${seconds}`;
};

/**
 * Хук живого таймера (секундомера)
 * @returns {{ elapsedMs: number, running: boolean, start: Function, stop: Function, reset: Function }}
 */
const useStopwatch = () => {
  const [elapsedMs, setElapsedMs] = useState(0);
  const [running, setRunning] = useState(false);
  const startTimeRef = useRef(null);
  const intervalRef = useRef(null);

  const clear = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    clear();
    setElapsedMs(0);
    startTimeRef.current = Date.now();
    setRunning(true);
    intervalRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startTimeRef.current);
    }, 100);
  }, [clear]);

  const stop = useCallback(() => {
    clear();
    if (startTimeRef.current) {
      setElapsedMs(Date.now() - startTimeRef.current);
    }
    setRunning(false);
  }, [clear]);

  const reset = useCallback(() => {
    clear();
    startTimeRef.current = null;
    setRunning(false);
    setElapsedMs(0);
  }, [clear]);

  useEffect(() => clear, [clear]);

  return { elapsedMs, running, start, stop, reset };
};

export default useStopwatch;
